import {
	NavLink,
	Navigate,
	href,
	Outlet,
	useOutletContext,
	useParams,
	useNavigate,
} from "react-router";
import { Fragment, useContext, useEffect, useState } from "react";
import { Context } from "../App";
import { AvatarWithPlaceholder } from "./AvatarWithPlaceholder";
import { CircularProgress, useMediaQuery } from "@mui/material";
import AppsIcon from "@mui/icons-material/AppsRounded";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faPlus,
	faGear,
	faCircleInfo,
	faMessage,
	faUserPlus,
} from "@fortawesome/free-solid-svg-icons";

import { SettingsContext } from "./Settings";
import { useChats } from "../util";

const keys = ["z", "x", "c", "v", "b", "n"];

export const Accounts = () => {
	const { accounts, setAccounts, persistence, setSettings } =
		useOutletContext<Context>();
	const params = useParams();
	const account = params.accountId ? accounts[params.accountId] : null;
	const navigate = useNavigate();
	const { messageRequests } = useContext(SettingsContext);

	const chats = useChats(messageRequests, account?.client);
	const anyApps = !!chats.find((c) => c.isApp());
	const anyInvites = !!chats.find((c) => c.__isInvite);
	const isMobile = useMediaQuery("(max-width: 576px)");

	useEffect(() => {
		if (!params.accountId) return;

		persistence.set("ui#selectedAccount", params.accountId);
	}, [params.accountId, persistence]);

	useEffect(() => {
		if (!account) return;

		const handler = () => {
			if (document.visibilityState === "hidden") {
				account.client.setNotInForeground();
			} else {
				account.client.setInForeground();
			}
		};

		window.addEventListener("visibilitychange", handler);
		handler();

		return () => {
			window.removeEventListener("visibilitychange", handler);
			account.client.setNotInForeground();
		};
	}, [account]);

	const [accountsUnread, setAccountsUnread] = useState<Record<string, number>>(
		{},
	);
	useEffect(() => {
		const tokens = Object.fromEntries(
			Object.keys(accounts).map((accountId) => {
				return [
					accountId,
					[
						accounts[accountId].client.addChatsUpdatedListener(() => {
							setAccountsUnread(
								Object.fromEntries(
									Object.keys(accounts).map((accountId) => [
										accountId,
										accounts[accountId].client
											.getChats()
											.map((c) => c.unreadCount())
											.reduce((x, y) => x + y, 0),
									]),
								),
							);
						}),
					],
				];
			}),
		);

		return () => {
			for (const [accountId, ts] of Object.entries(tokens)) {
				ts.map((t) => accounts[accountId].client.removeEventListener(t));
			}
		};
	}, [accounts]);
	const totalUnread = Object.values(accountsUnread).reduce((x, y) => x + y, 0);

	useEffect(() => {
		if (navigator.setAppBadge) {
			navigator.setAppBadge(totalUnread);
		}
		const icon = document.querySelector("link[rel*=icon]") as HTMLLinkElement;
		if (totalUnread < 1) {
			icon.href = "/favicon.ico";
		} else {
			const img = new Image();
			img.addEventListener("load", () => {
				const canvas = document.createElement("canvas");
				canvas.height = 64;
				canvas.width = 64;
				const ctx = canvas.getContext("2d")!;
				ctx.drawImage(img, 0, 0, 64, 64);
				ctx.fillStyle = "red";
				ctx.arc(64 - 17, 17, 17, 0, 2 * Math.PI, false);
				ctx.fill();
				ctx.font = "32px sans-serif";
				ctx.fillStyle = "white";
				ctx.textBaseline = "top";
				ctx.fillText("" + totalUnread, 64 - 28, 2, 26);
				icon.href = canvas.toDataURL();
			});
			img.src = icon.href;
		}
	}, [totalUnread]);

	const logout = async (completely: boolean) => {
		if (!params.accountId) throw "No account selected, cannot logout";

		accounts[params.accountId].client.logout(completely);

		if (navigator.serviceWorker) {
			const serviceWorkerRegistration = await navigator.serviceWorker.ready;
			const sub = await serviceWorkerRegistration.pushManager.getSubscription();
			if (!sub) return;
			await sub.unsubscribe(); // TODO: this isn't per account...
		}

		persistence.set("ui#selectedAccount", null);
		const newAccounts = { ...accounts };
		delete newAccounts[params.accountId];
		setAccounts(newAccounts);

		navigate("/");
	};

	const setStatus = (status: string) => {
		if (!params.accountId) return;

		setAccounts({
			...accounts,
			[params.accountId]: { ...accounts[params.accountId], status },
		});
	};

	if (!account?.client && params.accountId) {
		return <Navigate to="/" />;
	}

	return (
		<>
			<nav id="account-nav" aria-label="Account navigation" tabIndex={-1}>
				{Object.keys(accounts).map((accountId, idx) => {
					const client = accounts[accountId].client;
					const user = client
						.getChat(accountId)
						?.getParticipantDetails(accountId);
					return (
						<Fragment key={`account-${accountId}`}>
							<NavLink
								to={href("/accounts/:accountId", { accountId })}
								accessKey={keys[idx]}
							>
								<AvatarWithPlaceholder
									displayName={`Account: ${accountId}`}
									placeholderUri={user?.placeholderUri}
									photoUri={user?.photoUri}
									status={
										accounts[accountId].status === "online"
											? "available"
											: "unavailable"
									}
								/>
								{!accountsUnread[accountId] || accountsUnread[accountId] < 1 ? (
									""
								) : (
									<div
										className="cs-conversation__unread"
										aria-label={
											"" + accountsUnread[accountId] + " unread messages"
										}
									>
										{accountsUnread[accountId]}
									</div>
								)}
							</NavLink>
							{params.accountId === accountId &&
								(isMobile || anyApps || anyInvites) && (
									<NavLink
										to={href("/accounts/:accountId/chats", { accountId })}
									>
										<FontAwesomeIcon icon={faMessage} />
										<div className="label">Messages</div>
									</NavLink>
								)}
							{params.accountId === accountId && anyApps && (
								<NavLink to={href("/accounts/:accountId/apps", { accountId })}>
									<AppsIcon />
									<div className="label">Apps</div>
								</NavLink>
							)}
							{params.accountId === accountId && anyInvites && (
								<NavLink
									to={href("/accounts/:accountId/requests", { accountId })}
								>
									<FontAwesomeIcon icon={faUserPlus} />
									<div className="label">Requests</div>
								</NavLink>
							)}
						</Fragment>
					);
				})}
				<NavLink to="/login" title="Add Account">
					<FontAwesomeIcon icon={faPlus} />
				</NavLink>
				<NavLink to="/settings" title="Settings">
					<FontAwesomeIcon icon={faGear} />
				</NavLink>
				<NavLink to="/about" title="About">
					<FontAwesomeIcon icon={faCircleInfo} />
				</NavLink>
			</nav>
			{params.accountId &&
			accountsUnread[params.accountId] === undefined &&
			account?.status !== "auth" ? (
				<CircularProgress />
			) : (
				<Outlet
					context={{
						persistence,
						setSettings,
						client: account?.client,
						status: account?.status,
						setStatus,
						logout,
					}}
				/>
			)}
		</>
	);
};
